import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLButtonElement} [toggleButton] - Pause/play control for the looping video.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    // Keep the control in sync with the real playback state, including autoplay and browser-initiated pauses.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    videoElement.addEventListener('play', this.#syncToggle, { signal });
    videoElement.addEventListener('pause', this.#syncToggle, { signal });

    videoElement.load();
    this.#syncToggle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Pauses or resumes the background video.
   * @param {MouseEvent} [event]
   */
  togglePlayback(event) {
    event?.preventDefault();
    const { videoElement } = this.refs;

    if (videoElement.paused) {
      videoElement.play().catch(() => this.#syncToggle());
    } else {
      videoElement.pause();
    }
  }

  #syncToggle = () => {
    const { toggleButton, videoElement } = this.refs;
    if (!(toggleButton instanceof HTMLButtonElement)) return;

    const paused = videoElement.paused;
    const label = paused ? toggleButton.dataset.labelPlay : toggleButton.dataset.labelPause;
    if (label) toggleButton.setAttribute('aria-label', label);
    toggleButton.toggleAttribute('data-paused', paused);
  };
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
